export type { Connector, NativeTool, ConnectorType } from './types';
export { CONNECTORS, REKOR_TOOLSET_SERVICE_NAME } from './connectors';
export { NATIVE_TOOLS, SANDBOX_AGENT_ROLES, WAYAI_CONNECTOR } from './native-tools';
export type { ToolSideEffects } from './tool-side-effects';
export { httpToolSideEffects, catalogToolSideEffects, withComposedChain } from './tool-side-effects';
export type { ToolReplay } from './tool-replay';
export type { ReplayOverride, ToolSettingsKind } from './tool-replay';
export {
  REPLAY_OVERRIDES,
  TOOL_REPLAY_CLASSES,
  catalogToolReplay,
  httpToolReplay,
  idempotencyHeaderRefusal,
  isReplayOverride,
  withComposedChainReplay,
  withToolReplaySettings,
} from './tool-replay';
import type { AgentRole } from '../constants';
export type { LlmTokenCounts, LongPromptUsage, TtsBilledUsage } from './pricing';
export { MODEL_PRICING, computeCostUsd, longPromptSlice, sumLongPromptUsage } from './pricing';
export { STT_PRICING, computeSttCostUsd, TTS_PRICING, computeTtsCostUsd, reportedTokenCount } from './pricing';
export { CONTAINER_PRICING, computeContainerCostUsd } from './pricing';
export {
  VOICE_AGENT_ROLE,
  VOICE_AGENT_NEEDS_REALTIME_CONNECTION,
  REALTIME_CONNECTION_NEEDS_VOICE_AGENT,
  VOICE_AGENT_TEXT_EVAL_REFUSAL,
  CALLER_LANGUAGE,
  isVoiceAgentRole,
  isRealtimeConnectorType,
  bindableConnectorType,
  voiceAgentBindingRefusal,
  voiceAgentWriteNeedsGate,
} from './voice-agent';

import { CONNECTORS, REKOR_TOOLSET_SERVICE_NAME } from './connectors';
import { NATIVE_TOOLS } from './native-tools';
import type { Connector, NativeTool, ToolVariant } from './types';

/**
 * The retired `connector_type` tool values (pre-merge) mapped to their `tool_variant`.
 * Sole surviving source reference to the legacy literals — the CI name resolver
 * uses it so hub-as-code YAML committed before the merge keeps pushing.
 */
export const LEGACY_TOOL_CONNECTOR_TYPES: Record<string, ToolVariant> = {
  'Tool - Native': 'native',
  'Tool - Custom': 'custom',
  'Tool - MCP': 'mcp',
};

// Pre-built indexes for O(1) lookups
const connectorById = new Map<string, Connector>(CONNECTORS.map(c => [c.connector_id, c]));
const nativeToolById = new Map<string, NativeTool>(NATIVE_TOOLS.map(t => [t.tool_native_id, t]));

export function getConnectors(filter?: { connector_type?: string; service_name?: string }): Connector[] {
  if (!filter?.connector_type && !filter?.service_name) return CONNECTORS;
  return CONNECTORS.filter(c =>
    (!filter.connector_type || c.connector_type === filter.connector_type) &&
    (!filter.service_name || c.service_name === filter.service_name),
  );
}

export function getConnectorById(id: string): Connector | null {
  return connectorById.get(id) ?? null;
}

/** Catalog type of a connector (`Agent`, `Realtime`, …); null for an absent or unknown id. */
export function getConnectorType(connectorId: unknown): Connector['connector_type'] | null {
  if (typeof connectorId !== 'string') return null;
  return connectorById.get(connectorId)?.connector_type ?? null;
}

/** Whether this catalog entry is the base toolset connector (`REKOR_TOOLSET_SERVICE_NAME`). */
export function isRekorToolsetConnector(connector: { service_name?: unknown } | null | undefined): boolean {
  return connector?.service_name === REKOR_TOOLSET_SERVICE_NAME;
}

/** Whether a connection's `connector_id` names the base toolset connector. */
export function isRekorToolsetConnectorId(connectorId: unknown): boolean {
  return typeof connectorId === 'string' && isRekorToolsetConnector(connectorById.get(connectorId));
}

/** Delivery mechanism of a Tool connector; null for unknown ids and non-tool connectors. */
export function getToolVariant(connectorId: string | null | undefined): ToolVariant | null {
  if (!connectorId) return null;
  return connectorById.get(connectorId)?.tool_variant ?? null;
}

/**
 * Catalog listing of assignable native tools. A tool marked `assignable: false`
 * is a rollout-gated entry (its config plumbing shipped ahead of its handler) —
 * excluded here so it never surfaces in the UI "Add" grid, the CI name resolver,
 * or config snapshots. It remains reachable by id via `getNativeToolById` (schema
 * parity + handler dispatch). Flip the flag to make it live everywhere at once.
 */
export function getNativeTools(connectorId?: string): NativeTool[] {
  const assignable = NATIVE_TOOLS.filter(t => t.assignable !== false);
  if (!connectorId) return assignable;
  return assignable.filter(t => t.connector_id === connectorId);
}

export function getNativeToolById(id: string): NativeTool | null {
  return nativeToolById.get(id) ?? null;
}

/**
 * May a MODEL be offered this native tool? `false` only for a catalog entry that
 * says so; `true` for every other id, and for an id the catalog does not know.
 *
 * Takes the `tool_native_id` off a hub's own row rather than a name, because the
 * question is about the CATALOG entry the row installs — a hub row's name is
 * whatever the operator or an MCP server called it. An unknown id answers `true`:
 * an entry this catalog cannot see carries no restriction, which is the same
 * default `nativeToolRoleRefusal` applies for the same reason.
 */
export function catalogToolIsModelCallable(toolNativeId: unknown): boolean {
  if (typeof toolNativeId !== 'string') return true;
  return nativeToolById.get(toolNativeId)?.model_callable !== false;
}

/**
 * May an agent of this ROLE hold this native tool? `null` when it may; the
 * operator-facing refusal when it may not.
 *
 * THE ONE PREDICATE every path that can attach a CATALOG tool shares, so a tool
 * admitted on one surface and refused on another cannot happen. That divergence is
 * what this codebase's shared contract helpers exist to prevent; see
 * `isMonitorRuleCallableTool`, which states the same property for the rule
 * allowlist.
 *
 * THE PATHS ARE THREE, and naming them is the point — an earlier draft of this
 * comment said "all four assignment write paths" and was wrong in both directions:
 *
 *  1. `handleToolUpsert`, which the native REST path (`POST /native`) funnels through;
 *  2. `computeToolsDiff`'s native loop, for a tool added to an EXISTING agent;
 *  3. `buildToolPayloadsForNewAgent`'s native loop, for an agent the same push
 *     CREATES — whose tools never reach `handleToolUpsert` at all, but go
 *     `pendingByAgentName` → `apply-changes.ts` → a direct HubDO create. Missing it
 *     admits on create exactly what (2) refuses on update.
 *
 * `POST /mcp` and `POST /custom` are NOT among them and need no check: neither
 * attaches a catalog entry. A hub's own MCP or custom row that happens to be named
 * like a restricted native tool is not that tool, and refusing it would refuse a
 * tool the runtime correctly treats as ordinary.
 *
 * DECIDED FROM THE CATALOG ENTRY, never from a name. The caller resolves the entry
 * (`getNativeToolById`) and passes it, because only a catalog row can carry
 * `assignable_roles` — a hub's own tool row is whatever the operator or an MCP
 * server named it, and nothing reserves the native names.
 *
 * A tool with NO `assignable_roles` admits every role, which is every tool shipped
 * before the field existed, so this returns `null` for them without asking about
 * the role at all — which is also why a caller holding an unresolvable native id
 * may pass a bare `{ tool_name }`: an entry the catalog does not know carries no
 * restriction, and this function is where that policy is stated. An UNKNOWN or
 * absent role is refused only when the tool DOES restrict roles: a restricted tool
 * must not be attachable by a path that could not say what it was attaching to.
 */
export function nativeToolRoleRefusal(
  tool: Pick<NativeTool, 'tool_name'> & Partial<Pick<NativeTool, 'assignable_roles'>>,
  role: AgentRole | string | null | undefined,
): string | null {
  const roles = tool.assignable_roles;
  if (!roles || roles.length === 0) return null;
  if (role && (roles as readonly string[]).includes(role)) return null;
  return `"${tool.tool_name}" can only be assigned to an agent whose role is `
    + `${roles.join(' or ')}${role ? `; this agent's role is "${role}"` : ', and this agent\'s role could not be determined'}.`;
}
